import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import mergeWith from 'lodash/mergeWith'

import { Output, OutputRecipientList, OutputRecipientListInit, OutputRecipientListUpdate } from 'common/api/v1/types'
import { Api, AppDispatch, GlobalState, useRoutes } from '../../../store'
import { formTransform, useConfirmationDialog, useUser } from '../../../utils'
import {
  clearOutputList,
  createOutputList,
  getOutputList,
  removeOutputList,
  updateOutputList,
} from '../../../redux/actions/outputListsActions'
import { ButtonsPane, Form, GridItem, Paper, SafeRouting, TextInput } from '../../common/Form'
import DataSet from '../../common/DataSet'
import Pendable from '../../common/Pendable'
import Wrapper from '../../common/Wrapper'
import AvailableOutputs from './AvailableOutputs'
import Outputs from './Outputs'
import type { EnrichedOutputRecipientList } from '../../../api/nm-types'
import RHF, { FormProps } from '../../common/Form/RHF'

const { outputListsApi } = Api

const getInitialState = (
  group: string,
  selectedOutputList?: OutputRecipientList,
): OutputRecipientList & OutputRecipientListUpdate =>
  mergeWith(
    {
      name: '',
      description: '',
      group,
      addOutputs: [],
      removeOutputs: [],
    },
    selectedOutputList,
    (_initial, existent, key) => {
      if ('description' === key && !existent) {
        return ''
      }
    },
  )

const OutputListForm = ({
  getValues,
  setError,
  setFocus,
  formState,
  availableOutputs,
}: FormProps<EnrichedOutputRecipientList & OutputRecipientListUpdate> & { availableOutputs: number }) => {
  const values = getValues()
  const navigate = useNavigate()
  const routes = useRoutes()
  const dispatch = useDispatch<AppDispatch>()
  const showConfirm = useConfirmationDialog()
  const { formErrors, saving } = useSelector(
    ({ outputListsReducer }: GlobalState) => ({
      formErrors: outputListsReducer.formErrors,
      saving: outputListsReducer.saving,
    }),
    shallowEqual,
  )
  const [addIsShown, setAddIsShown] = useState(!values.id)
  const [outputsCollapsed, setOutputsCollapsed] = useState(false)

  const deleteOutputList = () => {
    showConfirm(() => {
      dispatch(removeOutputList(values.id))
    }, `Are you sure you want to delete output list ${values.name}?`)
  }

  useEffect(() => {
    if (Array.isArray(formErrors)) {
      formErrors.forEach((item) => {
        setError(item.name as keyof typeof values, { type: 'manual', message: item.reason })
      })
      if (formErrors[0]) setFocus(formErrors[0].name as keyof typeof values)
    }
  }, [formErrors, setError, setFocus])

  return (
    <>
      <SafeRouting formState={formState} />
      <Form id="output-list-form" noValidate>
        <Paper className="outlined" title="Metadata" collapsible>
          <Paper>
            <TextInput name="name" label="Name" required autoFocus />
            <TextInput name="description" label="Description" multiline />
          </Paper>

          {!!values.id && (
            <Paper>
              <GridItem lg={12} xl={12}>
                <DataSet
                  values={{
                    Id: values.id,
                    Group: values._group?.name ?? 'N/A',
                  }}
                />
              </GridItem>
            </Paper>
          )}
        </Paper>
        {!!values.id && (
          <Paper
            title="Outputs"
            collapsible
            collapsed={outputsCollapsed}
            actionsPane={
              !addIsShown && availableOutputs > 0
                ? [
                    {
                      id: 'add-outputs',
                      title: 'Add outputs',
                      onClick: () => {
                        setAddIsShown(true)
                        setOutputsCollapsed(true)
                      },
                    },
                  ]
                : []
            }
          >
            <Outputs />
          </Paper>
        )}
        {addIsShown && !saving && (
          <Paper title="Select outputs">
            <AvailableOutputs />
          </Paper>
        )}

        <ButtonsPane
          main={{
            Cancel: {
              onClick: () => void navigate(routes.outputLists()),
            },
            Save: { savingState: !!saving, primary: true, type: 'submit' },
          }}
          secondary={values.id ? { 'Remove output list': { onClick: deleteOutputList } } : undefined}
        />
      </Form>
    </>
  )
}

export const Edit = () => {
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const { outputList } = useSelector(
    ({ outputListsReducer }: GlobalState) => ({ outputList: outputListsReducer.outputList }),
    shallowEqual,
  )
  const { group } = useUser()
  const [availableOutputs, setAvailableOutputs] = useState(0)

  useEffect(() => {
    let unmounted = false

    if (id) {
      const params = { pageNumber: '0', rowsPerPage: '10' }
      const outputsInList = false
      const belongsToUserGroup = (output: Output) => output.group === group

      outputListsApi.getOutputs(id, outputsInList, params).then(({ items }) => {
        if (!unmounted) {
          setAvailableOutputs(items.filter(belongsToUserGroup).length)
        }
      })
    }

    return () => {
      unmounted = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    id && dispatch(getOutputList(id))
    return () => {
      dispatch(clearOutputList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (draft: Partial<OutputRecipientList & OutputRecipientListUpdate>) => {
    if (outputList) {
      dispatch(
        updateOutputList({
          outputList: draft as OutputRecipientList & OutputRecipientListUpdate,
          withRedirection: true,
        }),
      )
    } else {
      dispatch(createOutputList(draft as OutputRecipientListInit & Pick<OutputRecipientListUpdate, 'addOutputs'>))
    }
  }

  return (
    <Wrapper name={['Outputs', 'Lists', id ? outputList?.name : 'New']}>
      <Pendable pending={!!id && !outputList}>
        <RHF
          onSubmit={(values) => {
            onSubmit(formTransform(values))
          }}
          defaultValues={getInitialState(group, outputList)}
        >
          {(props) => <OutputListForm {...props} availableOutputs={availableOutputs} />}
        </RHF>
      </Pendable>
    </Wrapper>
  )
}
